import { useState } from 'react';
import axiosInstance from '../api/axiosInstance';

interface ClaimResult {
  claimType: string;
  incidentDate: string | null;
  summary: string;
  priority: 'Low' | 'Medium' | 'High';
  riskScore: number;
}

const ClaimsIntake = () => {
  const [description, setDescription] = useState('');
  const [result, setResult] = useState<ClaimResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    setError('');

    try {
      const response = await axiosInstance.post('/api/ai-claims/extract', {
  description,
});
      setResult(response.data.data);
    } catch (err: any) {
      console.error('Extract error:', err);
      setError(
        err.response?.data?.error ||
          'Failed to analyze claim. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="max-w-2xl w-full bg-white rounded-lg shadow p-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">
          Claims Intake Assistant
        </h1>
        <p className="text-gray-500 mb-6 text-sm">
          Paste an unstructured claim description. AI will extract structured data.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <textarea
            rows={6}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="e.g. My car was hit in a parking lot on 12 March. Rear bumper damaged. Police report filed."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
          />

          <button
            type="submit"
            disabled={loading || description.length < 10}
            className={`w-full py-2 px-4 rounded-md text-white font-medium ${
              loading || description.length < 10
                ? 'bg-indigo-300 cursor-not-allowed'
                : 'bg-indigo-600 hover:bg-indigo-700'
            }`}
          >
            {loading ? 'Analyzing with AI...' : 'Extract Claim Data'}
          </button>
        </form>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {error}
          </div>
        )}

        {result && (
          <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">
              Structured Output
            </h2>
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-gray-500">Claim Type</dt>
                <dd className="font-medium text-gray-900">{result.claimType}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Incident Date</dt>
                <dd className="font-medium text-gray-900">
                  {result.incidentDate || 'Not provided'}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Priority</dt>
                <dd className="font-medium text-gray-900">{result.priority}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Risk Score</dt>
                <dd className="font-medium text-gray-900">{result.riskScore}</dd>
              </div>
              <div className="col-span-2">
                <dt className="text-gray-500">Summary</dt>
                <dd className="font-medium text-gray-900">{result.summary}</dd>
              </div>
            </dl>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClaimsIntake;